import { forwardRef, type FocusEvent, type InputHTMLAttributes, type PointerEvent } from "react";
import { cn } from "@/lib/utils";

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

/**
 * Clase base de todo campo de formulario, compartida por `Input`, `Textarea` y
 * `SelectNativo` para que los tres se vean iguales sin copiar la cadena.
 *
 * `text-base` en mobile no es capricho: con menos de 16px iOS Safari hace zoom
 * automatico al enfocar el campo y descoloca toda la pantalla de carga.
 * El alto no va aca porque cada control lo pone distinto (`h-11` fijo en Input y
 * Select, `min-h-20` en Textarea).
 */
export const claseCampo =
  "flex w-full rounded-md border border-input bg-background px-3 text-base ring-offset-background transition-colors duration-150 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive sm:text-sm";

/**
 * Un campo de números (teclado numérico) marca todo lo que tiene al entrar,
 * así lo que se escribe lo reemplaza. En el celular el cursor quedaba donde
 * caía el dedo y lo nuevo se pegaba al número viejo («10» terminaba en «101»).
 * Se marca un instante después del foco porque en el iPhone, al soltar el
 * dedo, Safari vuelve a poner el cursor y desmarca lo que se marcó antes.
 */
function marcarTodoAlEntrar(e: FocusEvent<HTMLInputElement>): void {
  const input = e.currentTarget;
  const alEntrar = input.value;
  if (alEntrar === "") return;
  window.setTimeout(() => {
    // Si ya empezó a escribir, no se le marca lo escrito.
    if (document.activeElement === input && input.value === alEntrar) input.select();
  }, 0);
}

/**
 * El dedo toca un campo de números que YA tenía el foco (lo puso el cuadro al
 * abrirse, con `autoFocus`): el foco no vuelve a dispararse y no se marcaba
 * nada, así que lo escrito se pegaba («10» sobre «101» daba «10101»). Si
 * todavía no se escribió desde que entró el foco y no hay nada marcado, se
 * marca todo. Solo con el dedo: con el mouse, un clic pone el cursor donde se
 * quiere.
 */
function marcarTodoAlTocar(e: PointerEvent<HTMLInputElement>): void {
  const input = e.currentTarget;
  if (e.pointerType !== "touch" || input.value === "") return;
  if (input.dataset.escrito === "1") return;
  if (input.selectionStart !== input.selectionEnd) return;
  window.setTimeout(() => {
    if (document.activeElement === input) input.select();
  }, 0);
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, onFocus, onPointerUp, onInput, inputMode, ...props }, ref) => {
    const esNumero = inputMode === "decimal" || inputMode === "numeric";
    return (
      <input
        type={type}
        ref={ref}
        inputMode={inputMode}
        className={cn(claseCampo, "h-11 py-2 sm:h-10", className)}
        onFocus={
          esNumero
            ? (e) => {
                e.currentTarget.dataset.escrito = "";
                marcarTodoAlEntrar(e);
                onFocus?.(e);
              }
            : onFocus
        }
        onPointerUp={
          esNumero
            ? (e) => {
                marcarTodoAlTocar(e);
                onPointerUp?.(e);
              }
            : onPointerUp
        }
        onInput={
          esNumero
            ? (e) => {
                e.currentTarget.dataset.escrito = "1";
                onInput?.(e);
              }
            : onInput
        }
        {...props}
      />
    );
  },
);
Input.displayName = "Input";
